import { SnackbarKey } from 'notistack';
import { HttpTransportType, HubConnectionBuilder } from '@microsoft/signalr';
import {
    AppStore,
    INotification,
    Order,
    SignalRResponse,
    SignalRResponseAction,
    SignalRResponseMethod,
    Table,
    TableEvent,
    WorkingHoursType
} from '../types';
import { Action, ActionType, ThunkAction } from './actionTypes';
import * as Api from '../Api';
import { getTokenFromCookies } from '../helpers';

const ERROR_NOTIFICATION: INotification = {
    message: 'Something went wrong with your request',
    key: new Date().getTime(),
    options: {
        variant: 'error',
        autoHideDuration: 3000
    }
};

export const setProducts = (products: AppStore['products']): Action => ({
    type: ActionType.SET_PRODUCTS,
    payload: products
});

const setOpenOrders = (openOrders: AppStore['openOrders']): Action => ({
    type: ActionType.SET_OPEN_ORDERS,
    payload: openOrders
});

export const setNotDispatchedOrders = (
    notDispatchedOrders: AppStore['notDispatchedOrders']
): Action => ({
    type: ActionType.SET_NOT_DISPATCHED_ORDERS,
    payload: notDispatchedOrders
});

const setTables = (tables: AppStore['tables']): Action => ({
    type: ActionType.SET_TABLES,
    payload: tables
});

const setUser = (user: AppStore['user']): Action => ({
    type: ActionType.SET_USER,
    payload: user
});

const setTableEvents = (tableEvents: AppStore['tableEvents']): Action => ({
    type: ActionType.SET_TABLE_EVENTS,
    payload: tableEvents
});

const setConnection = (connection: AppStore['connection']): Action => ({
    type: ActionType.SET_CONNECTION,
    payload: connection
});

const setOrderDelivered = (order: Order): Action => ({
    type: ActionType.SET_ORDER_DELIVERED,
    payload: order
});

const addNewOrder = (order: Order): Action => ({
    type: ActionType.ADD_ORDER,
    payload: order
});

const setOrderCompleted = (order: Order): Action => ({
    type: ActionType.SET_ORDER_COMPLETED,
    payload: order
});

export const setWorkingHours = (workingHours: AppStore['workingHours']): Action => ({
    type: ActionType.SET_WORKING_HOURS,
    payload: workingHours
});

const addTableEvent = (tableEvent: TableEvent): Action => ({
    type: ActionType.ADD_TABLE_EVENT,
    payload: tableEvent
});

const removeTableEvent = (eventId: number): Action => ({
    type: ActionType.REMOVE_TABLE_EVENT,
    payload: eventId
});

const freeTable = (tableId: number): Action => ({
    type: ActionType.FREE_TABLE,
    payload: tableId
});

export const setSelectedTableOrder = (
    order: AppStore['selectedTableOrder']
): Action => ({
    type: ActionType.SET_SELECTED_TABLE_ORDER,
    payload: order
});

export const resetState = (): Action => ({
    type: ActionType.RESET_STATE
});

export const removeNotification = (key: SnackbarKey): Action => ({
    type: ActionType.REMOVE_NOTIFICATION,
    payload: key
});

export const enqueueNotification = (notification: INotification): Action => ({
    type: ActionType.ENQUEUE_NOTIFICATION,
    payload: notification
});

export const fetchData =
    (): ThunkAction<Promise<void>> => async (dispatch, getState) => {
        const {
            connection,
            openOrders,
            notDispatchedOrders,
            products,
            tables,
            user,
            tableEvents,
            workingHours
        } = getState().app;

        if (connection === null) {
            const signalRConnection = new HubConnectionBuilder()
                .withUrl('https://backbonedev.scanandorder.eu/signalR/BackofficeHub', {
                    skipNegotiation: true,
                    transport: HttpTransportType.WebSockets,
                    accessTokenFactory: () => getTokenFromCookies()
                })
                .withAutomaticReconnect()
                .build();

            signalRConnection.on(
                SignalRResponseMethod.ORDER_UPDATE,
                ({ action, entity, simpleID }: SignalRResponse) => {
                    switch (action) {
                        case SignalRResponseAction.NEW_ORDER:
                            dispatch(addNewOrder(entity));
                            break;
                        case SignalRResponseAction.ORDER_IS_PREPARED:
                            dispatch(setOrderCompleted(entity));
                            break;
                        case SignalRResponseAction.ORDER_IS_DELIVERED:
                            dispatch(setOrderDelivered(entity));
                            break;
                        case SignalRResponseAction.NEW_TABLE_EVENT:
                            dispatch(addTableEvent(entity));
                            break;
                        case SignalRResponseAction.TABLE_EVENT_IS_SEEN:
                            dispatch(removeTableEvent(simpleID));
                            break;
                        case SignalRResponseAction.TABLE_IS_PAID:
                            dispatch(freeTable(simpleID));
                            break;
                        default:
                            break;
                    }
                }
            );

            await signalRConnection.start();

            dispatch(setConnection(signalRConnection));
        }

        return Promise.all([
            openOrders ? Promise.resolve(openOrders) : Api.getOpenOrders(),
            notDispatchedOrders
                ? Promise.resolve(notDispatchedOrders)
                : Api.getNotDispatchedOrders(),
            products ? Promise.resolve(products) : Api.getProducts(),
            user ? Promise.resolve(user) : Api.getUserAccount(),
            tableEvents ? Promise.resolve(tableEvents) : Api.getOpenTableEvents(),
            workingHours ? Promise.resolve(workingHours) : Api.getWorkingHours(),
            tables
                ? Promise.resolve([
                      tables,
                      tables.filter(({ occupied }) => occupied).map(({ id }) => id)
                  ] as [Table[], number[]])
                : Promise.all([Api.getTables(), Api.getOccupiedTables()])
        ]).then(([oo, no, p, u, te, wh, [t, ot]]) => {
            dispatch(setOpenOrders(oo));
            dispatch(setNotDispatchedOrders(no));
            dispatch(setProducts(p));
            dispatch(setUser(u));
            dispatch(setTableEvents(te));
            dispatch(setWorkingHours(wh));
            dispatch(
                setTables(
                    t.map(table => ({
                        ...table,
                        occupied: ot.includes(table.id)
                    }))
                )
            );
        });
    };

export const initialRequest =
    (
        username: string,
        password: string,
        storeId: number
    ): ThunkAction<Promise<unknown>> =>
    async dispatch => {
        try {
            await Api.login(username, password, storeId);
        } catch (e) {
            dispatch(enqueueNotification(ERROR_NOTIFICATION));
            throw new Error(e);
        }

        return dispatch(fetchData());
    };

export const overrideWorkingHours =
    (type: WorkingHoursType): ThunkAction =>
    async dispatch => {
        await Api.overrideWorkingHours(type);

        Api.getWorkingHours().then(workingHours => {
            dispatch(setWorkingHours(workingHours));
        });
    };

export const selectTable =
    (tableId: number): ThunkAction<Promise<void>> =>
    dispatch =>
        Api.getTableOrder(tableId).then(order => {
            dispatch(setSelectedTableOrder(order));
        });
